/** Player store: load/migrate, mutate through update(), persist (debounced), notify subscribers. */
import { uuid } from './ids.js';
import { defaultState, migrate, type PlayerState } from './state.js';
import type { KV } from './storage.js';

const KEY = 'player';
const DEVICE_KEY = 'device';

export type Listener = (s: PlayerState) => void;

export class PlayerStore {
  private listeners = new Set<Listener>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  /** increments on every update; UI uses it to re-render */
  revision = 0;

  private constructor(
    private kv: KV,
    public state: PlayerState,
  ) {}

  /** A save exists on this device (false on the very first launch, or after the data was cleared). */
  static exists(kv: KV): boolean {
    return kv.get(KEY) !== null;
  }

  static load(kv: KV, now: number): PlayerStore {
    let deviceId = kv.get(DEVICE_KEY);
    if (!deviceId) {
      deviceId = uuid();
      kv.set(DEVICE_KEY, deviceId);
    }
    const raw = kv.get(KEY);
    const state = raw ? migrate(JSON.parse(raw) as Record<string, unknown>, deviceId, now) : defaultState(deviceId, now);
    const store = new PlayerStore(kv, state);
    if (raw) store.flush();
    return store;
  }

  /** Mutate the state in place; persists and notifies. */
  update(recipe: (s: PlayerState) => void): void {
    recipe(this.state);
    this.revision++;
    this.scheduleSave();
    for (const l of this.listeners) l(this.state);
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  flush(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = null;
    this.kv.set(KEY, JSON.stringify(this.state));
  }

  /** Wipe local progress (settings → delete account in R1; also used by tests). */
  reset(now: number): void {
    this.state = defaultState(this.state.deviceId, now);
    this.revision++;
    this.flush();
    for (const l of this.listeners) l(this.state);
  }

  private scheduleSave(): void {
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => this.flush(), 250);
  }
}
